import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, Check } from "lucide-react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { QuoteForm } from "@/components/QuoteForm";
import { Reviews } from "@/components/Reviews";
import { WhyChooseUs } from "@/components/WhyChooseUs";
import { Insurance } from "@/components/Insurance";
import { Pricing } from "@/components/Pricing";
import { Faq } from "@/components/Faq";
import { TrustBanner } from "@/components/TrustBanner";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { suburbSeo, SITE } from "@/data/seo";
import { SUBURBS, findSuburb } from "@/data/suburbs";

const SERVICE_POINTS = [
  "House & apartment moves",
  "Furniture removal & delivery",
  "Office & business relocation",
  "Local & interstate moves",
  "Professional packing & boxes",
  "Fully insured, careful crews",
];

export default function SuburbPage() {
  const { slug } = useParams();
  const suburb = findSuburb(slug);
  if (!suburb) return <Navigate to="/" replace />;

  const sameRegion = SUBURBS.filter((s) => s.slug !== slug && s.region === suburb.region);
  const others = SUBURBS.filter((s) => s.slug !== slug && s.region !== suburb.region);
  const nearby = [...sameRegion, ...others].slice(0, 14);
  const seo = suburbSeo(suburb.name, suburb.slug);

  return (
    <main data-testid="suburb-page">
      <Seo {...seo} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MovingCompany",
          "name": `MoversNearby — Removalists ${suburb.name}`,
          "url": `${SITE}/removalists/${suburb.slug}`,
          "logo": `${SITE}/images/logo-full.png`,
          "image": `${SITE}/images/hero-branded.png`,
          "telephone": "+61391136005",
          "priceRange": "$$",
          "areaServed": { "@type": "Place", "name": `${suburb.name}, Melbourne, VIC` },
          "address": { "@type": "PostalAddress", "addressLocality": suburb.name, "addressRegion": "VIC", "addressCountry": "AU" },
        })}</script>
      </Helmet>

      <Header />
      <Hero key={suburb.slug} suburb={suburb.name} />
      <QuoteForm />

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-20" data-testid="suburb-intro">
        <Reveal>
          <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-blaze">
            <MapPin className="h-4 w-4" /> Serving {suburb.name}
          </p>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Trusted Removalists &amp; Cheap Movers in {suburb.name}
          </h2>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-700">
            Need removalists near you in {suburb.name}? MoversNearby is a local Melbourne moving
            company helping {suburb.name} households and businesses move every week. From a single
            piece of furniture to a full home or office, our professional movers offer honest hourly
            rates from $69/hr, clear pricing and careful handling &mdash; with no hidden fees.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICE_POINTS.map((p, i) => (
            <Reveal key={p} delay={i * 0.05}>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm" data-testid={`suburb-point-${i}`}>
                <Check className="h-5 w-5 shrink-0 text-blue-600" />
                <span className="text-sm font-medium text-slate-800">{p} in {suburb.name}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Reviews />
      <WhyChooseUs />
      <Insurance />
      <Pricing />

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10" data-testid="suburb-nearby">
        <Reveal>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            Removalists in nearby Melbourne suburbs
          </h2>
        </Reveal>
        <div className="mt-6 flex flex-wrap gap-2.5">
          {nearby.map((s) => (
            <Link
              key={s.slug}
              to={`/removalists/${s.slug}`}
              data-testid={`nearby-link-${s.slug}`}
              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-blaze hover:text-blaze"
            >
              Removalists {s.name}
            </Link>
          ))}
        </div>
      </section>

      <Faq />
      <TrustBanner />
      <Footer />
    </main>
  );
}
